import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, X, Clock, TrendingUp } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { getDossiers } from '../services/api';
import { formatDateESTShort } from '../utils/dateUtils';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800',
  'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=800',
];

const getImageUrl = (item, index = 0) => {
  if (item?.cover_image) {
    if (item.cover_image.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${item.cover_image}`;
    }
    return item.cover_image;
  }
  return PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
};

// ============================================
// SEARCH PAGE - Forbes Style
// ============================================
const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allDossiers, setAllDossiers] = useState([]);

  useEffect(() => {
    const fetchDossiers = async () => {
      try {
        const data = await getDossiers();
        setAllDossiers(data || []);
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchDossiers();
  }, []);

  useEffect(() => {
    if (initialQuery) {
      handleSearch(initialQuery);
    }
  }, [initialQuery, allDossiers]);

  const handleSearch = (searchQuery) => {
    setLoading(true);
    const q = searchQuery.toLowerCase();
    
    const filtered = allDossiers.filter(d => 
      d.title?.toLowerCase().includes(q) ||
      d.content?.toLowerCase().includes(q) ||
      d.sector?.toLowerCase().includes(q) ||
      d.executive_summary?.some(s => s.toLowerCase().includes(q))
    );
    
    setResults(filtered);
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchParams({ q: query });
    handleSearch(query);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSearchParams({});
  };

  const trendingTopics = ['Trump', 'Bitcoin', 'Fed', 'China', 'IA'];

  return (
    <div className="min-h-screen bg-white">
      {/* Search Header */}
      <div className="bg-gray-900 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-white text-center mb-8" style={{ fontFamily: SERIF_FONT }}>
            Buscar
          </h1>
          <form onSubmit={handleSubmit} className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar noticias, temas, personas..."
              className="w-full pl-14 pr-12 py-4 text-lg rounded-xl border-0 focus:ring-2 focus:ring-blue-600"
              autoFocus
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </form>

          {/* Trending Topics */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <span className="text-gray-400 text-sm">Tendencias:</span>
            {trendingTopics.map((topic) => (
              <button
                key={topic}
                onClick={() => { setQuery(topic); handleSearch(topic); setSearchParams({ q: topic }); }}
                className="text-sm text-white hover:text-blue-400 transition-colors"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {query && (
          <p className="text-gray-600 mb-6">
            {loading ? 'Buscando...' : `${results.length} resultados para "${searchParams.get('q') || query}"`}
          </p>
        )}

        {results.length > 0 ? (
          <div className="space-y-6">
            {results.map((dossier, i) => (
              <Link
                key={dossier.dossier_id}
                to={`/dossiers/${dossier.dossier_id}`}
                className="flex gap-6 p-4 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                {/* Image */}
                <div className="w-48 h-32 flex-shrink-0 overflow-hidden bg-gray-100 rounded">
                  <img
                    src={getImageUrl(dossier, i)}
                    alt={dossier.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <Badge variant="outline" className="mb-2 capitalize">
                    {dossier.sector || 'Noticias'}
                  </Badge>
                  <h3
                    className="text-xl font-bold text-gray-900 group-hover:text-[#1e3a5f] transition-colors line-clamp-2 mb-2"
                    style={{ fontFamily: SERIF_FONT }}
                  >
                    {dossier.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-2 mb-2">
                    {dossier.what_changed_today || dossier.executive_summary?.[0]}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {formatDateESTShort(dossier.published_at || dossier.created_at)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : query && !loading ? (
          <div className="text-center py-12">
            <SearchIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No se encontraron resultados</p>
            <p className="text-sm text-gray-400 mt-2">Intenta con otros términos de búsqueda</p>
          </div>
        ) : !query ? (
          <div className="text-center py-12">
            <SearchIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Escribe algo para buscar</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchPage;
